import { todayMx } from '../lib/dateUtils';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Plus, MoreHorizontal, Edit, Trash2, Loader2, CheckCircle, Clock, AlertTriangle, Info, X } from 'lucide-react';

const prioConfig = {
  urgente:     { label: 'Urgente',     color: 'bg-red-500',    text: 'text-red-700 dark:text-red-400',    bg: 'bg-red-50 dark:bg-red-950/20',       border: 'border-l-red-500' },
  importante:  { label: 'Importante',  color: 'bg-amber-500',  text: 'text-amber-700 dark:text-amber-400',bg: 'bg-amber-50 dark:bg-amber-950/20',   border: 'border-l-amber-500' },
  normal:      { label: 'Normal',      color: 'bg-green-500',  text: 'text-green-700 dark:text-green-400',bg: 'bg-green-50 dark:bg-green-950/20',   border: 'border-l-green-500' },
  informacion: { label: 'Información', color: 'bg-blue-500',   text: 'text-blue-700 dark:text-blue-400',  bg: 'bg-blue-50 dark:bg-blue-950/20',     border: 'border-l-blue-500' },
};
const estatusConfig = {
  pendiente:  { label: 'Pendiente',   icon: Clock,       badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  en_proceso: { label: 'En proceso',  icon: AlertTriangle, badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  terminado:  { label: 'Terminado',   icon: CheckCircle, badge: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
};

export default function Pendientes() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterEstatus, setFilterEstatus] = useState('all');
  const [filterPrio, setFilterPrio] = useState('all');
  const [form, setForm] = useState({ titulo: '', descripcion: '', fecha: todayMx(), responsable: '', estatus: 'pendiente', prioridad: 'normal', client_id: '', client_name: '' });
  const [technicians, setTechnicians] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [searchingClient, setSearchingClient] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterEstatus !== 'all') params.append('estatus', filterEstatus);
      if (filterPrio !== 'all') params.append('prioridad', filterPrio);
      const [res, alertRes, techRes] = await Promise.all([
        axios.get(`${API}/pendientes?${params}`),
        axios.get(`${API}/stock/alerts`),
        axios.get(`${API}/technicians`)
      ]);
      setItems(res.data);
      setAlerts(alertRes.data);
      setTechnicians(techRes.data.filter(t => t.is_active !== false));
    } catch { toast.error('Error al cargar'); }
    finally { setLoading(false); }
  }, [filterEstatus, filterPrio]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.titulo.trim()) { toast.error('Título obligatorio'); return; }
    setSaving(true);
    try {
      if (selectedItem) {
        await axios.put(`${API}/pendientes/${selectedItem.id}`, form);
        toast.success('Actualizado');
      } else {
        await axios.post(`${API}/pendientes`, form);
        toast.success('Pendiente creado');
      }
      setDialogOpen(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`¿Eliminar "${item.titulo}"?`)) return;
    try { await axios.delete(`${API}/pendientes/${item.id}`); toast.success('Eliminado'); fetchData(); }
    catch { toast.error('Error'); }
  };

  const handleToggleStatus = async (item) => {
    const next = item.estatus === 'terminado' ? 'pendiente' : item.estatus === 'pendiente' ? 'en_proceso' : 'terminado';
    try { await axios.put(`${API}/pendientes/${item.id}`, { estatus: next }); fetchData(); }
    catch { toast.error('Error'); }
  };

  const openNew = () => {
    setSelectedItem(null);
    setForm({ titulo: '', descripcion: '', fecha: todayMx(), responsable: '', estatus: 'pendiente', prioridad: 'normal', client_id: '', client_name: '' });
    setClientSearch('');
    setClientResults([]);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setSelectedItem(item);
    setForm({ titulo: item.titulo, descripcion: item.descripcion || '', fecha: item.fecha || '', responsable: item.responsable || '', estatus: item.estatus, prioridad: item.prioridad, client_id: item.client_id || '', client_name: item.client_name || '' });
    setClientSearch(item.client_name || '');
    setClientResults([]);
    setDialogOpen(true);
  };

  const searchClients = async (q) => {
    setClientSearch(q);
    if (q.length < 2) { setClientResults([]); return; }
    setSearchingClient(true);
    try {
      const res = await axios.get(`${API}/clients?search=${encodeURIComponent(q)}`);
      setClientResults(res.data.slice(0, 8));
    } catch { setClientResults([]); }
    finally { setSearchingClient(false); }
  };

  const selectClient = (client) => {
    setForm({ ...form, client_id: client.id, client_name: client.full_name });
    setClientSearch(client.full_name);
    setClientResults([]);
  };

  const counts = {
    pendiente: items.filter(i => i.estatus === 'pendiente').length,
    en_proceso: items.filter(i => i.estatus === 'en_proceso').length,
    terminado: items.filter(i => i.estatus === 'terminado').length,
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="pendientes-page">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Pendientes / Notas</h2>
          <p className="text-muted-foreground text-sm">{items.length} notas · {counts.pendiente} pendientes · {counts.en_proceso} en proceso</p>
        </div>
        <Button onClick={openNew} data-testid="new-pendiente-btn"><Plus className="h-4 w-4 mr-2" />Nueva Nota</Button>
      </div>

      {/* Stock Alerts */}
      {alerts.length > 0 && (
        <Card className="border-red-300 dark:border-red-800 bg-red-50/80 dark:bg-red-950/20">
          <CardContent className="p-4">
            <p className="text-sm font-semibold text-red-700 dark:text-red-400 flex items-center gap-2 mb-2"><AlertTriangle className="h-4 w-4" />Alertas de Inventario ({alerts.length})</p>
            <div className="space-y-1">
              {alerts.map(a => (
                <div key={a.id} className="flex items-center justify-between text-sm p-2 bg-red-100/50 dark:bg-red-900/20 rounded">
                  <span className="font-medium text-red-800 dark:text-red-300">Stock bajo: {a.nombre}</span>
                  <span className="text-red-600 dark:text-red-400 font-mono">Quedan {a.en_tienda} (mín: {a.stock_minimo})</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: 'all', label: 'Todas' },
          { key: 'pendiente', label: `Pendientes (${counts.pendiente})`, color: 'text-amber-600' },
          { key: 'en_proceso', label: `En proceso (${counts.en_proceso})`, color: 'text-blue-600' },
          { key: 'terminado', label: `Terminados (${counts.terminado})`, color: 'text-green-600' },
        ].map(f => (
          <Button key={f.key} variant={filterEstatus === f.key ? 'default' : 'outline'} size="sm" className="rounded-full" onClick={() => setFilterEstatus(f.key)}>
            {f.label}
          </Button>
        ))}
        <div className="border-l mx-1" />
        {Object.entries(prioConfig).map(([key, cfg]) => (
          <Button key={key} variant={filterPrio === key ? 'default' : 'outline'} size="sm" className="rounded-full gap-1.5" onClick={() => setFilterPrio(filterPrio === key ? 'all' : key)}>
            <div className={`w-2.5 h-2.5 rounded-full ${cfg.color}`} />{cfg.label}
          </Button>
        ))}
      </div>

      {/* Notes List */}
      <div className="space-y-2">
        {items.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-muted-foreground">No hay pendientes</CardContent></Card>
        ) : items.map(item => {
          const prio = prioConfig[item.prioridad] || prioConfig.normal;
          const est = estatusConfig[item.estatus] || estatusConfig.pendiente;
          const EstatusIcon = est.icon;
          return (
            <Card key={item.id} className={`border-l-4 ${prio.border} ${item.estatus === 'terminado' ? 'opacity-60' : ''}`} data-testid={`pendiente-${item.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className={`font-semibold ${item.estatus === 'terminado' ? 'line-through text-muted-foreground' : ''}`}>{item.titulo}</h3>
                      <Badge className={`${est.badge} text-xs`}><EstatusIcon className="h-3 w-3 mr-1" />{est.label}</Badge>
                      <div className={`w-3 h-3 rounded-full ${prio.color}`} title={prio.label} />
                    </div>
                    {item.descripcion && <p className="text-sm text-muted-foreground mt-1">{item.descripcion}</p>}
                    <div className="flex gap-3 mt-2 text-xs text-muted-foreground">
                      {item.client_name && <span className="font-medium text-foreground">{item.client_name}</span>}
                      {item.fecha && <span>{item.client_name ? '· ' : ''}{item.fecha}</span>}
                      {item.responsable && <span>· {item.responsable}</span>}
                      {item.created_by && <span>· Por: {item.created_by}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleToggleStatus(item)} title="Cambiar estatus">
                      <CheckCircle className={`h-4 w-4 ${item.estatus === 'terminado' ? 'text-green-500' : 'text-muted-foreground'}`} />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(item)}><Edit className="h-4 w-4 mr-2" />Editar</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(item)} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" />Eliminar</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{selectedItem ? 'Editar' : 'Nueva'} Nota</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Cliente</Label>
              <div className="relative">
                <Input value={clientSearch} onChange={(e) => { searchClients(e.target.value); if (!e.target.value) setForm({...form, client_id: '', client_name: ''}); }} placeholder="Buscar por nombre o teléfono..." />
                {form.client_id && <Badge className="absolute right-2 top-1/2 -translate-y-1/2 text-xs bg-green-100 text-green-700 cursor-pointer" onClick={() => { setForm({...form, client_id: '', client_name: ''}); setClientSearch(''); }}>
                  {form.client_name} <X className="h-3 w-3 ml-1" />
                </Badge>}
                {clientResults.length > 0 && !form.client_id && (
                  <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {clientResults.map(c => (
                      <button key={c.id} type="button" onClick={() => selectClient(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors border-b last:border-0">
                        <span className="font-medium">{c.full_name}</span>
                        {c.phones?.length > 0 && <span className="text-muted-foreground ml-2">{c.phones[0]}</span>}
                      </button>
                    ))}
                  </div>
                )}
                {searchingClient && <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin" />}
              </div>
            </div>
            <div className="space-y-2"><Label>Título *</Label><Input value={form.titulo} onChange={(e) => setForm({...form, titulo: e.target.value})} required data-testid="pendiente-titulo" /></div>
            <div className="space-y-2"><Label>Descripción</Label><Textarea value={form.descripcion} onChange={(e) => setForm({...form, descripcion: e.target.value})} rows={3} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Fecha</Label><Input type="date" value={form.fecha} onChange={(e) => setForm({...form, fecha: e.target.value})} /></div>
              <div className="space-y-2"><Label>Responsable</Label>
                <select className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" value={form.responsable} onChange={(e) => setForm({...form, responsable: e.target.value})}>
                  <option value="">Seleccionar...</option>
                  {technicians.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                  <option value="Oficina">Oficina</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Prioridad</Label>
                <div className="flex gap-2">
                  {Object.entries(prioConfig).map(([key, cfg]) => (
                    <button key={key} type="button" onClick={() => setForm({...form, prioridad: key})}
                      className={`w-8 h-8 rounded-full ${cfg.color} transition-all ${form.prioridad === key ? 'ring-2 ring-offset-2 ring-primary scale-110' : 'opacity-50 hover:opacity-75'}`}
                      title={cfg.label} />
                  ))}
                </div>
              </div>
              <div className="space-y-2"><Label>Estatus</Label>
                <Select value={form.estatus} onValueChange={(v) => setForm({...form, estatus: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pendiente">Pendiente</SelectItem>
                    <SelectItem value="en_proceso">En proceso</SelectItem>
                    <SelectItem value="terminado">Terminado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
